/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: DevicesPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Devices module.
----------------------------------------------------------------*/

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Plus, Edit, Trash2, Search, Upload, Download, Monitor } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { useContextStore } from '../../store/context';

import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import * as api from './api';
import ImportDevicesModal from './ImportDevicesModal';

export default function DevicesPage() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const { selectedDomain } = useContextStore();
    const [devices, setDevices] = useState<api.Device[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Domain Contextual Filtering
    const effectiveDomainId = selectedDomain?.id;

    // Modals
    const [showImportModal, setShowImportModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; id: number | null }>({
        isOpen: false,
        id: null
    });
    const [isDeleting, setIsDeleting] = useState(false);

    // Grid State
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortBy, setSortBy] = useState<string>('macAddress');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

    const loadDevices = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await api.getDevices(effectiveDomainId);
            setDevices(data);
        } catch (error) {
            console.error('Failed to load devices', error);
            showError(t('provisioningDevices.loadError', 'Failed to load devices'));
        } finally {
            setIsLoading(false);
        }
    }, [effectiveDomainId, showError, t]);

    useEffect(() => {
        loadDevices();
    }, [loadDevices]);

    const handleDelete = async () => {
        if (!deleteModal.id) return;
        setIsDeleting(true);
        try {
            await api.deleteDevice(deleteModal.id);
            showSuccess(t('provisioningDevices.deleteSuccess', 'Device deleted successfully'));
            setDeleteModal({ isOpen: false, id: null });
            loadDevices();
        } catch (error) {
            showError(t('provisioningDevices.deleteError', 'Failed to delete device'));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleExport = () => {
        // Simple CSV export
        const headers = ['MAC Address', 'Description', 'Vendor', 'Model', 'Template', 'IP Address'];
        const rows = devices.map(d => [
            d.macAddress,
            d.description || '',
            d.vendor?.name || '',
            d.model?.model || '',
            d.template?.name || '',
            d.ipAddress || ''
        ]);

        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'devices_export.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Process Data (Client-side)
    const processedData = useMemo(() => {
        let filtered = [...devices];

        // Search
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            filtered = filtered.filter(item =>
                item.macAddress.toLowerCase().includes(q) ||
                (item.description && item.description.toLowerCase().includes(q)) ||
                (item.vendor?.name && item.vendor.name.toLowerCase().includes(q)) ||
                (item.model?.model && item.model.model.toLowerCase().includes(q))
            );
        }

        // Sort
        filtered.sort((a, b) => {
            let aValue: unknown = a[sortBy as keyof typeof a];
            let bValue: unknown = b[sortBy as keyof typeof b];

            // Handle nested fields
            if (sortBy === 'vendor.name') {
                aValue = a.vendor?.name || '';
                bValue = b.vendor?.name || '';
            } else if (sortBy === 'model.model') {
                aValue = a.model?.model || '';
                bValue = b.model?.model || '';
            } else if (sortBy === 'template.name') {
                aValue = a.template?.name || '';
                bValue = b.template?.name || '';
            }

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

        // Pagination
        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);

        return { data: paginatedData, totalItems, totalPages };
    }, [devices, searchQuery, sortBy, sortOrder, currentPage, itemsPerPage]);

    const handleColumnToggle = (key: string) => {
        setColumnVisibility(prev => ({
            ...prev,
            [key]: !(prev[key] ?? true)
        }));
    };

    const columnsDefinition = useMemo(() => [
        {
            key: 'macAddress',
            label: t('provisioningDevices.mac', 'MAC Address'),
            visible: true,
            sortable: true
        },
        {
            key: 'description',
            label: t('common.description', 'Description'),
            visible: true,
            sortable: true
        },
        {
            key: 'vendor.name',
            label: t('provisioningDevices.vendor', 'Vendor'),
            visible: true,
            sortable: true,
            render: (_: unknown, row: api.Device) => row.vendor?.name
        },
        {
            key: 'model.model',
            label: t('provisioningDevices.model', 'Model'),
            visible: true,
            sortable: true,
            render: (_: unknown, row: api.Device) => row.model?.model
        },
        {
            key: 'template.name',
            label: t('provisioningDevices.template', 'Template'),
            visible: true,
            sortable: true,
            render: (_: unknown, row: api.Device) => row.template?.name
        },
        {
            key: 'ipAddress',
            label: t('provisioningDevices.ip', 'IP Address'),
            visible: true,
            sortable: true
        },
        {
            key: 'actions',
            label: t('common.actions', 'Actions'),
            visible: true,
            sortable: false
        }
    ], [t]);

    const columns = useMemo(() => columnsDefinition.map(col => ({
        ...col,
        visible: columnVisibility[col.key] ?? col.visible
    })), [columnsDefinition, columnVisibility]);

    const renderActions = (row: api.Device) => (
        <div className="flex items-center justify-end gap-2">
            <SimpleTooltip content={t('common.edit', 'Edit')} position="top">
                <button
                    onClick={() => navigate(`/settings/provisioning/provisioning-devices/${row.uuid ?? row.id}`)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors text-blue-600 dark:text-blue-400"
                >
                    <Edit className="h-4 w-4" />
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={t('common.delete', 'Delete')} position="top">
                <button
                    onClick={() => setDeleteModal({ isOpen: true, id: row.id })}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors text-red-600 dark:text-red-400"
                >
                    <Trash2 className="h-4 w-4" />
                </button>
            </SimpleTooltip>
        </div>
    );

    return (
        <div className="p-6 space-y-6">
            <div className="h-2"></div>

            <DataGridSimple
                title={t('provisioningDevices.title', 'Provisioning Devices')}
                titleIcon={<Monitor className="h-5 w-5 text-primary" />} // Phone or Monitor
                data={processedData.data}
                columns={columns}
                loading={isLoading}
                renderActions={renderActions}

                // Custom actions in header
                actions={
                    <div className="flex items-center gap-2">
                        <SimpleTooltip content={t('provisioningDevices.scan', 'Scan Network')} position="top">
                            <button
                                onClick={() => navigate('/settings/provisioning/provisioning-devices/scan')}
                                className="flex items-center gap-2 px-3 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
                            >
                                <Search className="h-4 w-4" />
                                <span className="hidden sm:inline">{t('provisioningDevices.scan', 'Scan')}</span>
                            </button>
                        </SimpleTooltip>

                        <SimpleTooltip content={t('common.import', 'Import')} position="top">
                            <button
                                onClick={() => setShowImportModal(true)}
                                className="flex items-center gap-2 px-3 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
                            >
                                <Upload className="h-4 w-4" />
                            </button>
                        </SimpleTooltip>

                        <SimpleTooltip content={t('common.export', 'Export')} position="top">
                            <button
                                onClick={handleExport}
                                className="flex items-center gap-2 px-3 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
                            >
                                <Download className="h-4 w-4" />
                            </button>
                        </SimpleTooltip>

                        <SimpleTooltip content={t('common.add', 'Add')} position="top">
                            <button
                                onClick={() => navigate('/settings/provisioning/provisioning-devices/new')}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
                                <Plus className="h-4 w-4" /> {t('common.add', 'Add')}
                            </button>
                        </SimpleTooltip>
                    </div>
                }

                onColumnToggle={handleColumnToggle}

                searchQuery={searchQuery}
                onSearch={setSearchQuery}
                searchPlaceholder={t('common.search', 'Search...')}

                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={(column) => {
                    if (sortBy === column) {
                        setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                    } else {
                        setSortBy(column);
                        setSortOrder('asc');
                    }
                }}

                pagination={{
                    page: currentPage,
                    limit: itemsPerPage,
                    total: processedData.totalItems,
                    totalPages: processedData.totalPages
                }}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
            />

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, id: null })}
                onConfirm={handleDelete}
                title={t('provisioningDevices.delete', 'Delete Device')}
                message={t('common.confirmDelete', 'Are you sure you want to delete this item?')}
                confirmText={t('common.delete', 'Delete')}
                cancelText={t('common.cancel', 'Cancel')}
                type="danger"
                isLoading={isDeleting}
            />

            <ImportDevicesModal
                isOpen={showImportModal}
                onClose={() => {
                    setShowImportModal(false);
                    loadDevices(); // Reload after import
                }}
            />
        </div>
    );
}
